import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { matchingAPI, cvAPI } from '../services/api';
import { MEDIA_URL } from '../config';
import Sidebar from '../components/Sidebar';
import '../styles/theme.css';
import './MatchedJobs.css';

function MatchedJobs() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [error, setError] = useState('');
  const [reparseMsg, setReparseMsg] = useState('');
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => { fetchMatches(); }, []);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const response = await matchingAPI.getMyMatches();
      setMatches(response.data.matches || []);
    } catch { setMatches([]); }
    finally { setLoading(false); }
  };

  const handleMatchJobs = async () => {
    setMatching(true); setError(''); setReparseMsg('');
    try {
      const response = await matchingAPI.matchJobs({ top_n: 20 });
      setMatches(response.data.matches || []);
      setHasRun(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to match jobs. Make sure your CV is uploaded.');
    } finally { setMatching(false); }
  };

  const handleReparseCV = async () => {
    setReparsing(true); setError(''); setReparseMsg('');
    try {
      const res = await cvAPI.reparseCV();
      const { skills_extracted, experience_years } = res.data;
      setReparseMsg(`CV re-parsed: ${skills_extracted?.length || 0} skills found, ${experience_years || 0} years experience. Now click "Find New Matches".`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to re-parse CV.');
    } finally { setReparsing(false); }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return '#640ff7';
    if (score >= 65) return '#34f76b';
    if (score >= 50) return '#eef734';
    return '#f73444';
  };

  const getScoreBadge = (score) => {
    if (score >= 75) return { color: '#640ff7', label: 'Great Match' };
    if (score >= 65) return { color: '#34f76b', label: 'Good Match' };
    if (score >= 50) return { color: '#eef734', label: 'Fair Match' };
    return { color: '#f73444', label: 'Low Match' };
  };

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Matched Jobs</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1>Your Job Matches</h1>
              <p>AI-powered recommendations based on your CV</p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={handleReparseCV} disabled={reparsing}>
                <i className={`fas ${reparsing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
                {reparsing ? 'Re-parsing...' : 'Re-parse CV'}
              </button>
              <button className="btn btn-primary" onClick={handleMatchJobs} disabled={matching}>
                <i className={`fas ${matching ? 'fa-spinner fa-spin' : 'fa-magic'}`}></i>
                {matching ? 'Matching...' : 'Find New Matches'}
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {reparseMsg && (
            <div className="alert alert-success">
              <i className="fas fa-check-circle"></i> {reparseMsg}
            </div>
          )}

          {loading ? (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Loading matches...</p>
            </div>
          ) : matches.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-bullseye"></i>
              {hasRun ? (
                <>
                  <h3>No matches found</h3>
                  <p>Your CV didn't match any current job postings closely enough. Try updating your CV or check back when new jobs are posted.</p>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary" onClick={handleReparseCV} disabled={reparsing}>
                      <i className="fas fa-sync-alt"></i> Re-parse CV
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate('/jobs')}>
                      <i className="fas fa-search"></i> Browse All Jobs
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3>No matches yet</h3>
                  <p>Click "Find New Matches" to analyze available jobs and find your best fits</p>
                  <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={handleMatchJobs} disabled={matching}>
                    <i className="fas fa-magic"></i> Find Matches
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="matches-grid">
              {matches.filter(match => match.job).map(match => {
                const badge = getScoreBadge(match.match_score);
                const scoreColor = getScoreColor(match.match_score);
                // SVG ring: r=24, circumference = 2π*24 ≈ 150.8
                const R = 24, CIRC = 2 * Math.PI * R;
                const dash = (match.match_score / 100) * CIRC;
                const logoSrc = match.job?.company_logo
                  ? (match.job.company_logo.startsWith('http') ? match.job.company_logo : `${MEDIA_URL}${match.job.company_logo}`)
                  : null;
                return (
                  <div key={match.match_id} className="match-card card">
                    <div className="match-card-header">

                      {/* Score ring with company logo inside */}
                      <div className="match-score-wrap">
                        <svg width="60" height="60" viewBox="0 0 60 60">
                          {/* Track */}
                          <circle cx="30" cy="30" r={R} fill="none" stroke="var(--border)" strokeWidth="4" />
                          {/* Progress arc */}
                          <circle
                            cx="30" cy="30" r={R}
                            fill="none"
                            stroke={scoreColor}
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeDasharray={`${dash} ${CIRC}`}
                            transform="rotate(-90 30 30)"
                          />
                          {/* Clip circle for logo */}
                          <defs>
                            <clipPath id={`clip-${match.match_id}`}>
                              <circle cx="30" cy="30" r="20" />
                            </clipPath>
                          </defs>
                          {logoSrc ? (
                            <image
                              href={logoSrc}
                              x="10" y="10" width="40" height="40"
                              clipPath={`url(#clip-${match.match_id})`}
                              preserveAspectRatio="xMidYMid meet"
                            />
                          ) : (
                            <>
                              <circle cx="30" cy="30" r="20" fill="var(--surface-2)" />
                              <text x="30" y="35" textAnchor="middle" fontSize="14" fontWeight="700" fill="var(--primary)">
                                {match.job?.company?.charAt(0).toUpperCase()}
                              </text>
                            </>
                          )}
                        </svg>
                        <span className="match-score-pct" style={{ color: scoreColor }}>{match.match_score}%</span>
                      </div>

                      <div className="match-card-info">
                        <h3 className="match-job-title">{match.job?.title}</h3>
                        <div className="match-job-company">
                          <i className="fas fa-building"></i>
                          <span>{match.job?.company}</span>
                        </div>
                        <div className="match-job-location">
                          <i className="fas fa-map-marker-alt"></i> {match.job?.location || match.job?.city}
                        </div>
                      </div>
                      <span style={{
                        background: badge.color + '22',
                        color: badge.color,
                        border: `1px solid ${badge.color}55`,
                        borderRadius: '999px',
                        padding: '0.2rem 0.65rem',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>{badge.label}</span>                    </div>

                    {match.job?.salary && (
                      <div className="match-salary">
                        <i className="fas fa-money-bill-wave"></i> {match.job.salary}
                      </div>
                    )}

                    <div className="match-breakdown">
                      {[
                        { label: 'Skills Match', value: match.skill_match },
                        { label: 'Experience', value: match.experience_match },
                        { label: 'Text Relevance', value: match.text_similarity },
                      ].map(item => (
                        <div key={item.label} className="breakdown-row">
                          <span className="breakdown-label">{item.label}</span>
                          <div className="breakdown-bar">
                            <div className="breakdown-fill" style={{ width: `${item.value}%`, background: '#276da3ff' }}></div>
                          </div>
                          <span className="breakdown-pct">{item.value}%</span>
                        </div>
                      ))}
                    </div>

                    {match.matched_skills?.length > 0 && (
                      <div className="match-skills">
                        <span className="match-skills-label">Matching skills:</span>
                        <div className="match-skills-tags">
                          {match.matched_skills.slice(0, 5).map((skill, i) => (
                            <span key={i} className="badge badge-blue">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="match-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/jobs/${match.job?.id}`)}>
                        <i className="fas fa-eye"></i> View Job
                      </button>
                      {match.job?.url && (
                        <button className="btn btn-primary btn-sm" onClick={() => window.open(match.job.url, '_blank')}>
                          <i className="fas fa-external-link-alt"></i> Apply
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default MatchedJobs;
